import React, { useState } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface AttendanceRecord {
  id?: string;
  studentId: string;
  date: string;
  status: 'present' | 'absent' | 'late';
  remarks?: string;
}

interface StudentAttendanceProps {
  domain: string;
}

const StudentAttendance: React.FC<StudentAttendanceProps> = ({ domain }) => {
  const [studentId, setStudentId] = useState('');
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAttendance = async () => {
    if (!studentId.trim()) {
      setError('Please enter a student ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Using the correct multi-tenant structure: /school-users/PVSS/attendance
      const attendanceRef = collection(db, `school-users/${domain}/attendance`);
      const q = query(
        attendanceRef,
        where('studentId', '==', studentId.trim()),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);

      const records: AttendanceRecord[] = [];
      snapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() } as AttendanceRecord);
      });

      if (records.length === 0) {
        setError('No attendance records found for this student');
      }

      setAttendance(records);
    } catch (error: any) {
      console.error('Error loading attendance:', error);
      setError(`Failed to load attendance: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStats = () => {
    const present = attendance.filter(a => a.status === 'present').length;
    const absent = attendance.filter(a => a.status === 'absent').length;
    const late = attendance.filter(a => a.status === 'late').length;
    const total = attendance.length;
    const percentage = total > 0 ? ((present + late) / total * 100).toFixed(2) : 0;
    
    return { present, absent, late, total, percentage };
  };

  const stats = getAttendanceStats();

  return (
    <div className="row g-4">
      <div className="col-12">
        <div className="card shadow-sm">
          <div className="card-header bg-white py-3">
            <h5 className="mb-0">
              <i className="bi bi-calendar-check me-2 text-primary-custom"></i>
              Student Attendance
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Student ID"
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && loadAttendance()}
                  />
                  <button className="btn btn-primary" onClick={loadAttendance} disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search me-2"></i>
                        Load Attendance
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="alert alert-warning alert-dismissible fade show" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {attendance.length > 0 && (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <div className="card bg-success text-white">
                      <div className="card-body text-center">
                        <h3>{stats.present}</h3>
                        <p className="mb-0">Present</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-danger text-white">
                      <div className="card-body text-center">
                        <h3>{stats.absent}</h3>
                        <p className="mb-0">Absent</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-warning text-white">
                      <div className="card-body text-center">
                        <h3>{stats.late}</h3>
                        <p className="mb-0">Late</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-primary text-white">
                      <div className="card-body text-center">
                        <h3>{stats.percentage}%</h3>
                        <p className="mb-0">Attendance</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.map((record, idx) => (
                        <tr key={idx}>
                          <td>{new Date(record.date).toLocaleDateString()}</td>
                          <td>
                            <span className={`badge ${
                              record.status === 'present' ? 'bg-success' :
                              record.status === 'absent' ? 'bg-danger' : 'bg-warning'
                            }`}>
                              {record.status.toUpperCase()}
                            </span>
                          </td>
                          <td>{record.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;